// ═══════════════════════════════════════════════════════════════
// GeoOps — Data Sources Configuration
// Manage configurable camera sources and GIS layers.
// Replaces hardcoded GA/HI/WA 511 state-specific integrations.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, Button, Group, Stack, Badge, ActionIcon,
  Modal, TextInput, Select, Textarea, NumberInput, Tabs, Table,
  Anchor, Alert, Code, Loader, Center,
} from '../../components/gab/GabComponents';
import { useDisclosure } from '../../components/gab/GabHooks';
import { notifications } from '../../lib/notifications';
import {
  IconPlus, IconPencil, IconTrash, IconCamera, IconDatabase,
  IconPlugConnected, IconAlertCircle, IconCheck, IconRefresh, IconWand,
} from '@tabler/icons-react';
import { useGeoOpsDataStore, type CameraSource, type GISSource } from '../../store/geoopsDataStore';
import { fetchCamerasNearby, fetchGISFeatures } from '../../utils/perimeterCameras';

// ── Camera provider options ──────────────────────────────────

const PROVIDER_OPTIONS = [
  { value: 'generic_json', label: 'Generic JSON REST API' },
  { value: 'arcgis', label: 'ArcGIS MapServer / FeatureServer' },
  { value: 'generic_image', label: 'Single Camera Image URL' },
];

const PROTOCOL_OPTIONS = [
  { value: 'arcgis', label: 'ArcGIS REST Feature Service' },
  { value: 'wfs', label: 'WFS (Web Feature Service)' },
  { value: 'geojson', label: 'Static GeoJSON URL' },
];

const DEFAULT_FIELD_MAP = JSON.stringify({ lat: 'lat', lng: 'lng', name: 'name', id: 'id', imageUrl: 'imageUrl', streamUrl: '' }, null, 2);

// ── Presets ──────────────────────────────────────────────────
// Pre-configured settings for known camera data sources.
// All routes go through the worker proxy (/api/worker/...).

const CAMERA_PRESETS = [
  {
    // GA 511 response: { Id, Latitude, Longitude, Location, Roadway, Direction,
    //   Views: [{ Id, Url, Status }] }
    // Snapshot/stream URLs use Views[0].Id (the view ID), not the top-level camera Id.
    label: 'GA 511 (Georgia)',
    name: 'GA 511 Traffic Cameras',
    provider: 'generic_json',
    listEndpoint: '/api/worker/api/511ga/v2/cameras',
    snapshotEndpoint: '/api/worker/api/511ga/map/Cctv/{id}',
    streamEndpoint: '/api/worker/api/511ga/stream?imageId={id}',
    fieldMapping: JSON.stringify({ lat: 'Latitude', lng: 'Longitude', name: 'Location', id: 'Views.0.Id', imageUrl: '', streamUrl: '', roadway: 'Roadway', direction: 'Direction' }, null, 2),
    refreshInterval: 30,
    regionLat: '33.749',
    regionLng: '-84.388',
    regionRadius: '200',
  },
  {
    label: 'HI 511 (Hawaii)',
    name: 'HI 511 Traffic Cameras',
    provider: 'generic_json',
    listEndpoint: '/api/worker/api/511hi/cameras',
    snapshotEndpoint: '/api/worker/api/511hi/snapshot/{id}',
    streamEndpoint: '',
    fieldMapping: JSON.stringify({ lat: 'latitude', lng: 'longitude', name: 'name', id: 'id', imageUrl: '', streamUrl: '', roadway: 'roadway', direction: 'direction' }, null, 2),
    refreshInterval: 30,
    regionLat: '21.307',
    regionLng: '-157.858',
    regionRadius: '200',
  },
];

// ── Camera Source Form ───────────────────────────────────────

function CameraModal({
  opened, onClose, initial,
}: { opened: boolean; onClose: () => void; initial?: CameraSource }) {
  const { createCamera, updateCamera } = useGeoOpsDataStore();
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('generic_json');
  const [listEndpoint, setListEndpoint] = useState('');
  const [snapshotEndpoint, setSnapshotEndpoint] = useState('');
  const [streamEndpoint, setStreamEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [fieldMapping, setFieldMapping] = useState(DEFAULT_FIELD_MAP);
  const [refreshInterval, setRefreshInterval] = useState<number>(30);
  const [regionLat, setRegionLat] = useState('');
  const [regionLng, setRegionLng] = useState('');
  const [regionRadius, setRegionRadius] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyPreset = (preset: typeof CAMERA_PRESETS[number]) => {
    setName(preset.name);
    setProvider(preset.provider);
    setListEndpoint(preset.listEndpoint);
    setSnapshotEndpoint(preset.snapshotEndpoint);
    setStreamEndpoint(preset.streamEndpoint);
    setFieldMapping(preset.fieldMapping);
    setRefreshInterval(preset.refreshInterval);
    setRegionLat(preset.regionLat);
    setRegionLng(preset.regionLng);
    setRegionRadius(preset.regionRadius);
  };

  useEffect(() => {
    if (initial) {
      setName(initial.name);
      setProvider(initial.provider);
      setListEndpoint(initial.listEndpoint);
      setSnapshotEndpoint(initial.snapshotEndpoint);
      setStreamEndpoint(initial.streamEndpoint);
      setApiKey(initial.apiKey);
      setFieldMapping(initial.fieldMapping || DEFAULT_FIELD_MAP);
      setRefreshInterval(initial.refreshInterval);
      setRegionLat(initial.regionLat != null ? String(initial.regionLat) : '');
      setRegionLng(initial.regionLng != null ? String(initial.regionLng) : '');
      setRegionRadius(initial.regionRadius != null ? String(initial.regionRadius) : '');
    } else {
      setName(''); setProvider('generic_json'); setListEndpoint('');
      setSnapshotEndpoint(''); setStreamEndpoint(''); setApiKey('');
      setFieldMapping(DEFAULT_FIELD_MAP); setRefreshInterval(30);
      setRegionLat(''); setRegionLng(''); setRegionRadius('');
    }
  }, [initial, opened]);

  // Warn if the user entered an external URL that should go through the proxy
  const needsProxy = listEndpoint.startsWith('http') && !listEndpoint.startsWith(window.location.origin);

  const handleTest = async () => {
    if (!listEndpoint) return;
    setTesting(true);
    try {
      const src: CameraSource = {
        id: 0, name, provider, listEndpoint, snapshotEndpoint, streamEndpoint,
        apiKey, fieldMapping, refreshInterval,
        regionLat: regionLat ? Number(regionLat) : null,
        regionLng: regionLng ? Number(regionLng) : null,
        regionRadius: regionRadius ? Number(regionRadius) : null,
        status: 'active',
      };
      const cams = await fetchCamerasNearby({ lat: 33.749, lng: -84.388 }, 500, [src]);
      notifications.show({
        title: 'Connection successful',
        message: `Returned ${cams.length} cameras from ${name || 'endpoint'}.`,
        color: 'green', icon: <IconCheck size={14} />,
      });
    } catch (e) {
      notifications.show({ title: 'Connection failed', message: String(e), color: 'red' });
    }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!name.trim() || !listEndpoint.trim()) {
      notifications.show({ message: 'Source name and list endpoint are required.', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: name.trim(), provider, listEndpoint: listEndpoint.trim(),
        snapshotEndpoint: snapshotEndpoint.trim(), streamEndpoint: streamEndpoint.trim(),
        apiKey: apiKey.trim(), fieldMapping,
        refreshInterval,
        regionLat: regionLat ? Number(regionLat) : null,
        regionLng: regionLng ? Number(regionLng) : null,
        regionRadius: regionRadius ? Number(regionRadius) : null,
        status: 'active',
      };
      if (initial) {
        await updateCamera(initial.id, data);
        notifications.show({ message: 'Camera source updated.', color: 'green' });
      } else {
        await createCamera(data);
        notifications.show({ message: 'Camera source added.', color: 'green' });
      }
      onClose();
    } catch (e) {
      notifications.show({ title: 'Save failed', message: String(e), color: 'red' });
    }
    setSaving(false);
  };

  return (
    <Modal opened={opened} onClose={onClose}
      title={initial ? 'Edit Camera Source' : 'Add Camera Source'}
      size="lg" centered>
      <Stack gap="sm">
        {!initial && (
          <div>
            <Text size="xs" fw={500} c="dimmed" mb={4}>Quick-start presets</Text>
            <Group gap="xs">
              {CAMERA_PRESETS.map(p => (
                <Button key={p.label} size="xs" variant="light" leftSection={<IconWand size={12} />}
                  onClick={() => applyPreset(p)}>
                  {p.label}
                </Button>
              ))}
            </Group>
          </div>
        )}
        <Group grow>
          <TextInput label="Source Name" required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. County Traffic Cameras" />
          <Select label="Provider Type" data={PROVIDER_OPTIONS} value={provider} onChange={v => setProvider(v ?? 'generic_json')} />
        </Group>

        <TextInput
          label={provider === 'generic_image' ? 'Image URL (direct snapshot)' : 'List Endpoint URL'}
          required
          value={listEndpoint}
          onChange={e => setListEndpoint(e.target.value)}
          placeholder={provider === 'arcgis'
            ? 'https://services.arcgis.com/.../FeatureServer/0'
            : provider === 'generic_image'
              ? 'https://cameras.example.com/cam1.jpg'
              : 'https://511api.example.com/api/v2/cameras'}
        />

        {needsProxy && (
          <Alert color="yellow" icon={<IconAlertCircle size={14} />}>
            External URLs require CORS support from the remote server. For sources that need server-side authentication (like 511 APIs), use the proxy path instead:{' '}
            <Code>/api/worker/api/511ga/v2/cameras</Code>
          </Alert>
        )}

        {provider !== 'generic_image' && (
          <Group grow>
            <TextInput
              label="Snapshot URL Template"
              value={snapshotEndpoint}
              onChange={e => setSnapshotEndpoint(e.target.value)}
              placeholder="https://cameras.example.com/snapshot/{id}.jpg"
              description="Use {id} for camera ID. Leave blank to use imageUrl from field mapping."
            />
            <TextInput
              label="Stream URL Template (HLS/RTSP)"
              value={streamEndpoint}
              onChange={e => setStreamEndpoint(e.target.value)}
              placeholder="https://streams.example.com/{id}/playlist.m3u8"
            />
          </Group>
        )}

        <Group grow>
          <TextInput label="API Key / Token" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="Optional" />
          <NumberInput label="Refresh Interval (seconds)" value={refreshInterval} onChange={v => setRefreshInterval(Number(v) || 30)} min={5} max={3600} />
        </Group>

        {provider !== 'arcgis' && (
          <Textarea
            label="Field Mapping (JSON)"
            value={fieldMapping}
            onChange={e => setFieldMapping(e.target.value)}
            minRows={5}
            autosize
            description="Map response fields → lat, lng, name, id, imageUrl, streamUrl"
            styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          />
        )}

        {provider === 'generic_image' && (
          <Alert color="blue" icon={<IconCamera size={14} />}>
            Single-image source — set region coordinates to place the camera pin on the map.
          </Alert>
        )}

        <Text size="xs" fw={500}>Bounding Region (optional — restricts which cameras are shown per zone scan)</Text>
        <Group grow>
          <TextInput label="Center Latitude" value={regionLat} onChange={e => setRegionLat(e.target.value)} placeholder="33.749" />
          <TextInput label="Center Longitude" value={regionLng} onChange={e => setRegionLng(e.target.value)} placeholder="-84.388" />
          <TextInput label="Radius (miles)" value={regionRadius} onChange={e => setRegionRadius(e.target.value)} placeholder="50" />
        </Group>

        <Group justify="space-between" mt="sm">
          <Button variant="light" leftSection={<IconPlugConnected size={14} />}
            onClick={handleTest} loading={testing} disabled={!listEndpoint}>
            Test Connection
          </Button>
          <Group>
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {initial ? 'Update' : 'Add Source'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── GIS Layer Form ───────────────────────────────────────────

function GISModal({
  opened, onClose, initial,
}: { opened: boolean; onClose: () => void; initial?: GISSource }) {
  const { createGISLayer, updateGISLayer } = useGeoOpsDataStore();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [protocol, setProtocol] = useState('arcgis');
  const [layerName, setLayerName] = useState('');
  const [authType, setAuthType] = useState<'none' | 'token' | 'basic'>('none');
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [categoryMapping, setCategoryMapping] = useState('{"*": "Infrastructure"}');
  const [refreshInterval, setRefreshInterval] = useState<number>(60);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setName(initial.name); setUrl(initial.url); setProtocol(initial.protocol);
      setLayerName(initial.layerName); setCategoryMapping(initial.categoryMapping || '{"*": "Infrastructure"}');
      setRefreshInterval(initial.refreshInterval);
      try {
        const a = JSON.parse(initial.auth || '{}');
        setAuthType(a.type || 'none');
        setToken(a.token || ''); setUsername(a.username || ''); setPassword(a.password || '');
      } catch { setAuthType('none'); }
    } else {
      setName(''); setUrl(''); setProtocol('arcgis'); setLayerName('');
      setCategoryMapping('{"*": "Infrastructure"}'); setRefreshInterval(60);
      setAuthType('none'); setToken(''); setUsername(''); setPassword('');
    }
  }, [initial, opened]);

  const buildAuth = () => JSON.stringify({
    type: authType,
    ...(authType === 'token' ? { token } : {}),
    ...(authType === 'basic' ? { username, password } : {}),
  });

  const handleTest = async () => {
    if (!url) return;
    setTesting(true);
    try {
      const src: GISSource = {
        id: 0, name, url, protocol, layerName, auth: buildAuth(),
        categoryMapping, refreshInterval, status: 'active', lastSynced: '', featureCount: 0,
      };
      const features = await fetchGISFeatures(src);
      notifications.show({
        title: 'Connection successful',
        message: `Found ${features.length} features from ${name || url}.`,
        color: 'green', icon: <IconCheck size={14} />,
      });
    } catch (e) {
      notifications.show({ title: 'Connection failed', message: String(e), color: 'red' });
    }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!name.trim() || !url.trim()) {
      notifications.show({ message: 'Layer name and URL are required.', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      const data: Omit<GISSource, 'id'> = {
        name: name.trim(), url: url.trim(), protocol, layerName: layerName.trim(),
        auth: buildAuth(), categoryMapping, refreshInterval,
        status: 'active', lastSynced: '', featureCount: 0,
      };
      if (initial) {
        await updateGISLayer(initial.id, data);
        notifications.show({ message: 'GIS layer updated.', color: 'green' });
      } else {
        await createGISLayer(data);
        notifications.show({ message: 'GIS layer added.', color: 'green' });
      }
      onClose();
    } catch (e) {
      notifications.show({ title: 'Save failed', message: String(e), color: 'red' });
    }
    setSaving(false);
  };

  return (
    <Modal opened={opened} onClose={onClose}
      title={initial ? 'Edit GIS Layer' : 'Add GIS Layer'}
      size="lg" centered>
      <Stack gap="sm">
        <Group grow>
          <TextInput label="Layer Name" required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Critical Infrastructure" />
          <Select label="Protocol" data={PROTOCOL_OPTIONS} value={protocol} onChange={v => setProtocol(v ?? 'arcgis')} />
        </Group>

        <TextInput
          label="URL"
          required
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder={
            protocol === 'arcgis'
              ? 'https://services.arcgis.com/.../FeatureServer/0'
              : protocol === 'wfs'
                ? 'https://gis.example.com/geoserver/ows'
                : 'https://example.com/infrastructure.geojson'
          }
        />

        {protocol === 'wfs' && (
          <TextInput
            label="WFS Type Name / Layer"
            value={layerName}
            onChange={e => setLayerName(e.target.value)}
            placeholder="e.g. cite:critical_infrastructure"
          />
        )}

        <Select
          label="Authentication"
          data={[
            { value: 'none', label: 'None (public)' },
            { value: 'token', label: 'Bearer Token' },
            { value: 'basic', label: 'Basic Auth (username / password)' },
          ]}
          value={authType}
          onChange={v => setAuthType((v as 'none' | 'token' | 'basic') ?? 'none')}
        />
        {authType === 'token' && (
          <TextInput label="Token" value={token} onChange={e => setToken(e.target.value)} placeholder="Bearer token or API key" />
        )}
        {authType === 'basic' && (
          <Group grow>
            <TextInput label="Username" value={username} onChange={e => setUsername(e.target.value)} />
            <TextInput label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} />
          </Group>
        )}

        <Textarea
          label="Category Mapping (JSON)"
          value={categoryMapping}
          onChange={e => setCategoryMapping(e.target.value)}
          minRows={3}
          autosize
          description={'Map feature attribute values to POI category names. Use "*" as a catch-all.'}
          styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
        />

        <NumberInput label="Refresh Interval (minutes)" value={refreshInterval} onChange={v => setRefreshInterval(Number(v) || 60)} min={1} />

        <Alert color="blue" icon={<IconDatabase size={14} />}>
          Features are fetched at scan time for the zone being analyzed. Only Point geometry features are shown as POI pins.
        </Alert>

        <Group justify="space-between" mt="sm">
          <Button variant="light" leftSection={<IconPlugConnected size={14} />}
            onClick={handleTest} loading={testing} disabled={!url}>
            Test Connection
          </Button>
          <Group>
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {initial ? 'Update' : 'Add Layer'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Main Page ────────────────────────────────────────────────

export default function DataSources() {
  const { cameras, gisLayers, loading, loadCameras, deleteCamera, loadGISLayers, deleteGISLayer } = useGeoOpsDataStore();
  const [activeTab, setActiveTab] = useState<string | null>('cameras');

  const [cameraModal, { open: openCamera, close: closeCamera }] = useDisclosure(false);
  const [gisModal, { open: openGIS, close: closeGIS }] = useDisclosure(false);
  const [editCamera, setEditCamera] = useState<CameraSource | undefined>();
  const [editGIS, setEditGIS] = useState<GISSource | undefined>();

  useEffect(() => {
    loadCameras();
    loadGISLayers();
  }, []);

  const handleEditCamera = (c: CameraSource) => { setEditCamera(c); openCamera(); };
  const handleAddCamera = () => { setEditCamera(undefined); openCamera(); };
  const handleDeleteCamera = async (id: number) => {
    if (!confirm('Delete this camera source?')) return;
    await deleteCamera(id);
    notifications.show({ message: 'Camera source removed.', color: 'orange' });
  };

  const handleEditGIS = (g: GISSource) => { setEditGIS(g); openGIS(); };
  const handleAddGIS = () => { setEditGIS(undefined); openGIS(); };
  const handleDeleteGIS = async (id: number) => {
    if (!confirm('Delete this GIS layer?')) return;
    await deleteGISLayer(id);
    notifications.show({ message: 'GIS layer removed.', color: 'orange' });
  };

  const providerLabel = (p: string) => PROVIDER_OPTIONS.find(o => o.value === p)?.label ?? p;
  const protocolLabel = (p: string) => PROTOCOL_OPTIONS.find(o => o.value === p)?.label ?? p;

  if (loading && cameras.length === 0 && gisLayers.length === 0) {
    return <Center h={200}><Loader /></Center>;
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Data Sources</Title>
          <Text size="sm" c="dimmed">Configure camera feeds and GIS layers for zone scanning</Text>
        </div>
      </Group>

      <Alert color="blue" icon={<IconAlertCircle size={14} />} mb="md">
        Camera sources and GIS layers replace state-specific 511 integrations. Add any provider with a REST or GeoJSON endpoint — cameras appear in zone scans, GIS layers add infrastructure POIs to your operational maps.
      </Alert>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="cameras" leftSection={<IconCamera size={14} />}>
            Camera Sources {cameras.length > 0 && <Badge ml="xs" size="xs" variant="light">{cameras.length}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="gis" leftSection={<IconDatabase size={14} />}>
            GIS Layers {gisLayers.length > 0 && <Badge ml="xs" size="xs" variant="light">{gisLayers.length}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="help" leftSection={<IconPlugConnected size={14} />}>Integration Guide</Tabs.Tab>
        </Tabs.List>

        {/* Camera Sources */}
        <Tabs.Panel value="cameras">
          <Group justify="flex-end" mb="md">
            <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={loadCameras}>Refresh</Button>
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={handleAddCamera}>Add Camera Source</Button>
          </Group>

          {cameras.length === 0 ? (
            <Paper p="xl" withBorder ta="center">
              <IconCamera size={32} color="gray" style={{ marginBottom: 8 }} />
              <Text c="dimmed" mb="sm">No camera sources configured</Text>
              <Text size="sm" c="dimmed" mb="md">Add a traffic camera provider — any REST API that returns camera locations with image URLs.</Text>
              <Button size="sm" leftSection={<IconPlus size={14} />} onClick={handleAddCamera}>Add First Camera Source</Button>
            </Paper>
          ) : (
            <Paper withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Source Name</Table.Th>
                    <Table.Th>Provider</Table.Th>
                    <Table.Th>Endpoint</Table.Th>
                    <Table.Th>Refresh</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {cameras.map(c => (
                    <Table.Tr key={c.id}>
                      <Table.Td fw={500}>{c.name}</Table.Td>
                      <Table.Td><Badge variant="light" size="sm">{providerLabel(c.provider)}</Badge></Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.listEndpoint}
                        </Text>
                      </Table.Td>
                      <Table.Td>{c.refreshInterval}s</Table.Td>
                      <Table.Td>
                        <Badge color={c.status === 'active' ? 'green' : 'gray'} size="sm">{c.status}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end">
                          <ActionIcon variant="subtle" onClick={() => handleEditCamera(c)}><IconPencil size={14} /></ActionIcon>
                          <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteCamera(c.id)}><IconTrash size={14} /></ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Tabs.Panel>

        {/* GIS Layers */}
        <Tabs.Panel value="gis">
          <Group justify="flex-end" mb="md">
            <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={loadGISLayers}>Refresh</Button>
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={handleAddGIS}>Add GIS Layer</Button>
          </Group>

          {gisLayers.length === 0 ? (
            <Paper p="xl" withBorder ta="center">
              <IconDatabase size={32} color="gray" style={{ marginBottom: 8 }} />
              <Text c="dimmed" mb="sm">No GIS layers connected</Text>
              <Text size="sm" c="dimmed" mb="md">Connect ArcGIS Feature Services, WFS servers, or static GeoJSON to overlay critical infrastructure, utilities, and facilities on zone maps.</Text>
              <Button size="sm" leftSection={<IconPlus size={14} />} onClick={handleAddGIS}>Add First GIS Layer</Button>
            </Paper>
          ) : (
            <Paper withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Layer Name</Table.Th>
                    <Table.Th>Protocol</Table.Th>
                    <Table.Th>URL</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {gisLayers.map(g => (
                    <Table.Tr key={g.id}>
                      <Table.Td fw={500}>{g.name}</Table.Td>
                      <Table.Td><Badge variant="light" color="indigo" size="sm">{protocolLabel(g.protocol)}</Badge></Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {g.url}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={g.status === 'active' ? 'green' : g.status === 'error' ? 'red' : 'gray'} size="sm">{g.status}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end">
                          <ActionIcon variant="subtle" onClick={() => handleEditGIS(g)}><IconPencil size={14} /></ActionIcon>
                          <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteGIS(g.id)}><IconTrash size={14} /></ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Tabs.Panel>

        {/* Integration Guide */}
        <Tabs.Panel value="help">
          <Stack gap="md">
            <Paper p="md" withBorder>
              <Text fw={600} mb="xs">Camera Sources — Supported Provider Types</Text>
              <Stack gap="xs">
                <div>
                  <Badge variant="light" mb={4}>Generic JSON REST API</Badge>
                  <Text size="sm">Any REST endpoint that returns a JSON array of camera objects. Use field mapping to tell the system which fields contain lat, lng, name, id, and image URL.</Text>
                  <Code block mt="xs">{`Field Mapping example:
{ "lat": "Latitude", "lng": "Longitude", "name": "CameraName", "id": "CameraID", "imageUrl": "SnapshotURL" }`}</Code>
                </div>
                <div>
                  <Badge variant="light" color="indigo" mb={4}>ArcGIS MapServer / FeatureServer</Badge>
                  <Text size="sm">Paste the Feature Service URL (ending in /0 or /FeatureServer/0). The system appends <Code>/query?where=1=1&outFields=*&f=json</Code> automatically. Field mapping determines which attributes contain coordinates and image URLs.</Text>
                </div>
                <div>
                  <Badge variant="light" color="teal" mb={4}>Single Camera Image URL</Badge>
                  <Text size="sm">A direct image URL (JPEG, PNG, MJPEG). Set region lat/lng to place the camera pin on the map. Use the stream URL field for an HLS/RTSP stream.</Text>
                </div>
              </Stack>
            </Paper>

            <Paper p="md" withBorder>
              <Text fw={600} mb="xs">GIS Layers — How Scanning Works</Text>
              <Text size="sm" mb="xs">When a zone scan runs, the system fetches all active GIS layers and filters for features whose Point geometry falls within the zone boundary. Features are displayed as POI pins alongside HERE-sourced POIs.</Text>
              <Stack gap="xs">
                <div>
                  <Badge variant="light" color="indigo" mb={4}>ArcGIS REST Feature Service</Badge>
                  <Text size="sm">Paste the layer URL. The system queries: <Code>/query?where=1=1&outFields=*&f=geojson</Code></Text>
                </div>
                <div>
                  <Badge variant="light" color="grape" mb={4}>WFS (Web Feature Service)</Badge>
                  <Text size="sm">Provide the WFS endpoint and type name. The system sends a GetFeature request with <Code>outputFormat=application/json</Code>.</Text>
                </div>
                <div>
                  <Badge variant="light" color="teal" mb={4}>Static GeoJSON</Badge>
                  <Text size="sm">Direct link to a GeoJSON FeatureCollection. Fetched fresh at each scan.</Text>
                </div>
              </Stack>
            </Paper>

            <Paper p="md" withBorder>
              <Text fw={600} mb="xs">Category Mapping</Text>
              <Text size="sm" mb="xs">Map feature attribute values to POI category names used in the zone scan display. Use <Code>"*"</Code> as a catch-all default.</Text>
              <Code block>{`Example — mapping a "FACILITY_TYPE" attribute:
{
  "Hospital": "Hospitals",
  "Fire Station": "Fire Stations",
  "Police": "Police Stations",
  "*": "Infrastructure"
}`}</Code>
              <Text size="xs" c="dimmed" mt="xs">
                The system scans all attribute values for matches. If no match is found, the catch-all <Code>*</Code> is used.
                See <Anchor href="https://docs.opengov.com" target="_blank" size="xs">documentation</Anchor> for the full category list.
              </Text>
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <CameraModal opened={cameraModal} onClose={() => { closeCamera(); setEditCamera(undefined); }} initial={editCamera} />
      <GISModal opened={gisModal} onClose={() => { closeGIS(); setEditGIS(undefined); }} initial={editGIS} />
    </div>
  );
}
